import { SavedChartState } from '../types';

export const CURRENT_CHART_SCHEMA_VERSION = 2;
const INITIAL_CHART_SCHEMA_VERSION = 1;

const getChartSchemaVersion = (chart: SavedChartState): number => {
  const raw = chart.chartSchemaVersion;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 1) {
    return Math.floor(raw);
  }
  return INITIAL_CHART_SCHEMA_VERSION;
};

const migrateV1ToV2 = (chart: SavedChartState): SavedChartState => {
  const migrated: SavedChartState = JSON.parse(JSON.stringify(chart));

  migrated.sourceChart = migrated.sourceChart.map((measure, index, all) => {
    const annotations = measure.annotations;
    if (!annotations) return measure;

    // Infer explicit barline side from legacy fields when explicit values are missing.
    if (!annotations.barlineLeft && !annotations.barlineRight) {
      if (annotations.doubleBarline) {
        const prevHasEndRepeat = index > 0 && !!all[index - 1]?.annotations?.endRepeat;
        const shouldBeLeft = !!annotations.ending || prevHasEndRepeat;

        if (shouldBeLeft && !annotations.startRepeat) {
          annotations.barlineLeft = 'double';
        } else if (!annotations.endRepeat) {
          annotations.barlineRight = 'double';
        }
      }

      if (annotations.finalBarline) {
        if (!annotations.endRepeat) {
          annotations.barlineRight = 'final';
        } else if (!annotations.startRepeat) {
          annotations.barlineLeft = 'final';
        }
      }
    }

    // Enforce side exclusivity in migrated data.
    if (annotations.startRepeat && annotations.barlineLeft) {
      delete annotations.barlineLeft;
    }
    if (annotations.endRepeat && annotations.barlineRight) {
      delete annotations.barlineRight;
    }

    return measure;
  });

  migrated.chartSchemaVersion = 2;
  return migrated;
};

export const migrateChartToCurrent = (chart: SavedChartState): SavedChartState => {
  let working: SavedChartState = chart;
  let version = getChartSchemaVersion(working);

  if (version > CURRENT_CHART_SCHEMA_VERSION) {
    return { ...working, chartSchemaVersion: version };
  }

  while (version < CURRENT_CHART_SCHEMA_VERSION) {
    if (version === 1) {
      working = migrateV1ToV2(working);
      version = 2;
      continue;
    }

    // Safety break for unknown intermediate versions.
    break;
  }

  return {
    ...working,
    chartSchemaVersion: Math.max(version, CURRENT_CHART_SCHEMA_VERSION),
  };
};

export const withCurrentChartSchemaVersion = (chart: SavedChartState): SavedChartState => ({
  ...chart,
  chartSchemaVersion: CURRENT_CHART_SCHEMA_VERSION,
});
